import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod/v4";

import { useProfile } from "./get-profile";
import { createUserRequestFromPersonalInfo, updateUser } from "./utils";

import type { MutationConfig } from "@/lib/react-query";
import type { User } from "@/types/user";

export const userSchema = z.object({
  title: z.string().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  gender: z.enum(["male", "female", "non-binary", "prefer-not-to-say"]).optional(),
  date_of_birth: z.string().optional(), // ISO 8601 date string
  place_of_birth: z.string().optional(),
  citizenship: z.string().optional(),
});

export type UpdatePersonalInfoInput = z.infer<typeof userSchema>;

type UseUpdatePersonalInfoOptions = {
  mutationConfig?: MutationConfig<(data: UpdatePersonalInfoInput) => Promise<User>>;
};

export const useUpdatePersonalInfo = ({ mutationConfig }: UseUpdatePersonalInfoOptions = {}) => {
  const queryClient = useQueryClient();
  const { data: user } = useProfile();
  const createRequest = createUserRequestFromPersonalInfo(user);

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: (data) => updateUser(createRequest(data)),
    onSuccess: (data, variables, onMutateResult, context) => {
      const previous = queryClient.getQueryData<User | undefined>(["profile"]);

      const merged: User = {
        ...(previous ?? (data as User)),
        ...(data ?? {}),
        additional_info: {
          ...(previous?.additional_info ?? {}),
          ...(data?.additional_info ?? {}),
        },
      } as User;

      queryClient.setQueryData(["profile"], merged);
      onSuccess?.(data, variables, onMutateResult, context);
    },
    ...restConfig,
  });
};
